import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Nova } from '../design/Nova';
import { Icon } from '../design/icons';
import { IconButton } from '../design/primitives/Button';
import { Switch } from '../design/primitives/Input';
import { tipsForPath, areaKey } from './tips';

// Nova the assistant — a helper, NOT Clippy's heckler. She sits quietly in the corner and
// only ever opens when clicked. The single proactive thing she does is, the FIRST time you
// land on a kind of screen, float one small non-blocking tip that auto-dismisses and never
// repeats — and that can be switched off. She never covers content, never steals focus, and
// hides to a tiny paw with one click (brought back the same way). All state is per-browser
// (localStorage); nothing here calls the network.

const LS = {
  hidden: 'nova.assistant.hidden',
  autoTips: 'nova.assistant.autotips',
  seen: 'nova.assistant.seen',
};

function readBool(key: string, dflt: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? dflt : v === '1';
  } catch {
    return dflt;
  }
}
function writeBool(key: string, v: boolean): void {
  try {
    localStorage.setItem(key, v ? '1' : '0');
  } catch {
    /* private mode / blocked storage — fine, defaults apply */
  }
}
function readSeen(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(LS.seen) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}
function markSeen(key: string): void {
  try {
    const s = readSeen();
    s.add(key);
    localStorage.setItem(LS.seen, JSON.stringify([...s]));
  } catch {
    /* ignore */
  }
}

export function NovaAssistant() {
  const { pathname } = useLocation();
  const [hidden, setHidden] = useState(() => readBool(LS.hidden, false));
  const [autoTips, setAutoTips] = useState(() => readBool(LS.autoTips, true));
  const [open, setOpen] = useState(false);
  const [nudge, setNudge] = useState<string | null>(null);
  const nudgeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const panelRef = useRef<HTMLDivElement>(null);

  const clearNudge = useCallback(() => {
    setNudge(null);
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
  }, []);

  // One-time gentle nudge on arriving at a new kind of screen.
  useEffect(() => {
    if (hidden || open || !autoTips) return;
    const key = areaKey(pathname);
    if (readSeen().has(key)) return;
    const tip = tipsForPath(pathname)[0];
    if (!tip) return;
    const show = setTimeout(() => {
      setNudge(tip.text);
      markSeen(key);
      nudgeTimer.current = setTimeout(() => setNudge(null), 9000);
    }, 900); // let the screen settle first
    return () => clearTimeout(show);
  }, [pathname, hidden, open, autoTips]);

  // Escape closes the panel; click-away too.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const hide = () => {
    setOpen(false);
    setHidden(true);
    writeBool(LS.hidden, true);
  };
  const unhide = () => {
    setHidden(false);
    writeBool(LS.hidden, false);
  };
  const toggleAuto = (v: boolean) => {
    setAutoTips(v);
    writeBool(LS.autoTips, v);
  };

  if (hidden) {
    return (
      <button type="button" className="nova-paw" onClick={unhide} aria-label="Show Nova, your guide" title="Show Nova">
        <Nova variant="mark" size={18} />
      </button>
    );
  }

  const tips = tipsForPath(pathname);

  return (
    <div className="nova-assistant" ref={panelRef}>
      {nudge && !open && (
        <div className="nova-bubble" role="status">
          <span>{nudge}</span>
          <IconButton icon="x" label="Dismiss tip" size="sm" onClick={clearNudge} />
        </div>
      )}

      {open && (
        <div className="nova-panel" role="dialog" aria-label="Nova, your guide">
          <header className="nova-panel-head">
            <Nova variant="mark" mood="happy" size={30} />
            <div className="nova-panel-title">
              <strong className="hf-display">Nova</strong>
              <span className="hf-muted">Your guide</span>
            </div>
            <IconButton icon="x" label="Close" size="sm" onClick={() => setOpen(false)} />
          </header>
          <div className="nova-panel-body">
            <p className="nova-panel-lead">Here on this screen:</p>
            <ul className="nova-tips">
              {tips.map((t, i) => (
                <li key={i}>
                  <Icon name="lightbulb" size={13} className="hf-muted" />
                  <span>{t.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <footer className="nova-panel-foot">
            <Switch checked={autoTips} onChange={(e) => toggleAuto(e.currentTarget.checked)} label="Tip me when I open a new screen" />
            <button type="button" className="nova-hide-link" onClick={hide}>
              Hide Nova
            </button>
          </footer>
        </div>
      )}

      <button
        type="button"
        className="nova-fab"
        aria-label={open ? 'Close Nova' : 'Open Nova, your guide'}
        aria-expanded={open}
        onClick={() => {
          clearNudge();
          setOpen((v) => !v);
        }}
      >
        <Nova variant="full" mood={open ? 'happy' : 'wave'} size={46} animated={!open} />
      </button>
    </div>
  );
}
